import group from '@tk/utilities/tk.array.group';
import TKCustomElementFactory from '@tk/utilities/tk.custom.element.factory';
import { fetchRequest } from '@tk/utilities/tk.fetch';
import { compareMessages } from '@tk/utilities/tk.messages';
import render from '@tk/utilities/tk.render';
import TKCompareStore, { Article, AttributeResponse, CompareActions } from './tk.compare.store';
import { useStore, waitForStore } from '@tk/utilities/tk.use.store';
import { fromBase64 } from '@tk/utilities/tk.base64';

export interface AttributeItem {
    attribute: string;
    label: string;
}

export interface AttributeGroup {
    id: number;
    title: string;
    attributes: AttributeItem[];
}

export interface TileResponse {
    id: number;
    tileHTML: string;
    tileHeaderHTML: string;
}

export default class TKCompareOverview extends TKCustomElementFactory {
    showHeaderClassName: string;
    hideCategoryClassName: string;
    headerHeightVariableName: string;
    articlesURL?: string;
    attributesURL?: string;
    tilesURL?: string;
    store?: TKCompareStore;
    differenceClassName: string;
    emptyClassName: string;
    emptyAttributesClassName: string;
    loadingClassName: string;
    cacheDurationInHours: number;
    protected unsubscribe?: () => void;

    constructor() {
        super();

        this.showHeaderClassName = this.getDataAttribute('show-header-class-name', 'tk-compare-overview--show-header');
        this.hideCategoryClassName = this.getDataAttribute(
            'hide-category-class-name',
            'tk-compare-overview--hide-category',
        );
        this.headerHeightVariableName = this.getDataAttribute('header-height-variable-name', '--compare-header-height');
        this.differenceClassName = this.getDataAttribute('difference-class-name', 'tk-compare-overview__difference');
        this.emptyClassName = this.getDataAttribute('empty-class-name', 'tk-compare-overview--empty');
        this.emptyAttributesClassName = this.getDataAttribute(
            'empty-class-name',
            'tk-compare-overview--empty-attributes',
        );
        this.loadingClassName = this.getDataAttribute('loading-class-name', 'tk-compare-overview--loading');

        this.articlesURL = this.getAttribute('data-tk-articles-url') || undefined;
        this.attributesURL = this.getAttribute('data-tk-attributes-url') || undefined;
        this.tilesURL = this.getAttribute('data-tk-tiles-url') || undefined;
        this.cacheDurationInHours = Number(this.getAttribute('data-tk-cache-duration-in-hour') || 6);
    }

    async connectedCallback() {
        if (!this.articlesURL || !this.attributesURL || !this.tilesURL) return;
        await waitForStore('compareStore');
        this.store = useStore('compareStore') as TKCompareStore;
        await this.initializeData();
        this.initializeUI();
        this.registerScrollListener();
    }

    disconnectedCallback(): void {
        this.unsubscribe && this.unsubscribe();
    }

    async initializeData() {
        await Promise.all([this.getArticlesByURL(), this.getAttributesByURL()]);
    }

    initializeUI() {
        const root = document.documentElement;
        const { currentCategoryId } = this.store!.getState();
        const currentCategory = this.store?.getCategoryById(currentCategoryId);
        const articleCount = this.store?.getArticleCountByCategoryId(currentCategory?.id || 0) || 0;

        if (articleCount === 0) this.classList.add(this.emptyClassName);
        root.style.setProperty('--count-articles', `${articleCount}`);

        articleCount > 0 && this.renderAttributeList();
        this.unsubscribe = this.store?.subscribe(({ action }) => this.rerender(action as CompareActions));
        this.classList.remove(this.loadingClassName);
    }

    registerScrollListener() {
        const onScroll = this.handleScrolling.bind(this);
        this.pushListener({
            event: 'scroll',
            element: this,
            action: onScroll,
        });
    }

    handleScrolling() {
        const headerHeight = parseFloat(window.getComputedStyle(this).getPropertyValue(this.headerHeightVariableName));
        this.classList.toggle(this.showHeaderClassName, this.scrollTop > headerHeight);
        this.classList.toggle(this.hideCategoryClassName, this.scrollTop > headerHeight);
    }

    articlesToSync(articleIds: number[]): { articlesToUpdate: number[]; articlesToAdd: number[] } {
        if (!this.store) return { articlesToUpdate: [], articlesToAdd: articleIds };
        const { articles } = this.store.getState();
        const existingArticles = articles.filter((article) => articleIds.includes(article.id));
        const notExistingArticles = articleIds.filter((id) => !articles.map((item) => item.id).includes(id));
        if (existingArticles.length === 0) return { articlesToUpdate: [], articlesToAdd: articleIds };
        return {
            articlesToUpdate: existingArticles
                .filter(
                    (item) =>
                        (item.createdAt || 0) + this.cacheDurationInHours * 1000 * 360 < Date.now() ||
                        !('tileHTML' in item) ||
                        !('tileHeaderHTML' in item),
                )
                .map((item) => item.id),
            articlesToAdd: notExistingArticles,
        };
    }

    async getArticlesByURL() {
        if (!this.store) return;
        const { currentCategoryId } = this.store.getState();
        const currentCategory = this.store.getCategoryById(currentCategoryId);
        if (!currentCategory) return;
        const { articlesToUpdate, articlesToAdd } = this.articlesToSync(currentCategory.articleIds);

        if (articlesToUpdate.length === 0) {
            const articles = this.getSortedArticlesByCategory(currentCategory.id).map(
                (item) =>
                    ({
                        id: item.id,
                        tileHTML: item.tileHTML,
                        tileHeaderHTML: item.tileHeaderHTML,
                    }) as TileResponse,
            );
            this.renderTiles(articles, true);
        }

        if (articlesToAdd.length === 0 && articlesToUpdate.length === 0) return;
        const articles = [...articlesToAdd, ...articlesToUpdate].join(',');
        await fetchRequest({
            requestURL: this.articlesURL!,
            payload: {
                articles,
                categoryId: String(currentCategoryId),
            },
            resolveHandler: this.handleArticleResponse.bind(this),
        });
        await fetchRequest({
            requestURL: this.tilesURL!,
            payload: {
                articles,
            },
            resolveHandler: this.handleTilesResponse.bind(this),
        });
    }

    async getAttributesByURL() {
        if (!this.store) return;
        const { currentCategoryId } = this.store.getState();
        const payload = {
            categoryId: String(currentCategoryId),
        };
        await fetchRequest({
            requestURL: this.attributesURL!,
            payload,
            resolveHandler: this.handleAttributeResponse.bind(this),
        });
    }

    async handleArticleResponse(response: TKResponse<Article[]>) {
        if (!response || !response.success) return;
        const articles = response.dataAsJson;
        this.store?.addMultipleItems<Article>('articles', articles);
    }

    async handleAttributeResponse(response: TKResponse<AttributeResponse[]>) {
        if (!response || !response.success) return;
        const attributes = response.dataAsJson;
        this.store?.addMultipleItems<AttributeResponse>('attributeList', attributes);
    }

    handleTilesResponse(response: TKResponse<TileResponse[]>) {
        if (!response || !response.success) return;
        const articles = response.dataAsJson;
        this.renderTiles(articles);
    }

    renderTiles(articles: TileResponse[], forceClear?: boolean) {
        const tilesWrapper = this.querySelector<HTMLElement>('[data-tk-tiles-wrapper]');
        const tilesHeaderWrapper = this.querySelector<HTMLElement>('[data-tk-tiles-header-wrapper]');
        if (!tilesWrapper || !tilesHeaderWrapper) return;

        this.updateTiles(tilesWrapper, tilesHeaderWrapper, articles, forceClear);
    }

    updateTiles(
        tilesWrapper: HTMLElement,
        tilesHeaderWrapper: HTMLElement,
        articles: TileResponse[],
        forceClear?: boolean,
    ) {
        if (forceClear) {
            tilesWrapper.innerHTML = '';
            tilesHeaderWrapper.innerHTML = '';
        }
        articles.forEach(async ({ id, tileHTML, tileHeaderHTML }) => {
            tilesWrapper.appendChild(render(fromBase64(tileHTML), false, true));
            tilesHeaderWrapper.appendChild(render(fromBase64(tileHeaderHTML), false, true));
            await this.store?.modifyItem('articles', id, { tileHTML, tileHeaderHTML, createdAt: Date.now() });
        });
    }

    async renderAttributeList() {
        if (!this.store) return;

        const wrapper = this.querySelector<HTMLElement>('[data-tk-attribute-list-wrapper]');
        if (!wrapper) return;

        const { currentCategoryId } = this.store.getState();
        const attributeGroups = await this.store.getEntriesByCategoryId(currentCategoryId);
        if (!attributeGroups) return;

        const preparedGroups = this.prepareAttributeGroups(attributeGroups);
        const sortedArticles = this.getSortedArticlesByCategory(currentCategoryId);

        const root = document.documentElement;
        root.style.setProperty('--count-groups', `${preparedGroups.length}`);
        this.classList.toggle('tk-compare-overview--empty-attributes', preparedGroups.length === 0);

        wrapper.innerHTML = '';
        if (preparedGroups.length === 0) {
            wrapper.appendChild(this.renderEmptyAttributeListMessage());
        } else {
            this.renderGroups(wrapper, preparedGroups, sortedArticles);
            this.compare();
        }
    }

    prepareAttributeGroups(attributes: AttributeResponse[]): AttributeGroup[] {
        const importantAttributes = attributes.filter((item) => item.isImportant);
        const groupedAttributes = group(attributes, 'groupId');
        const groupedList: AttributeGroup[] = [];

        if (importantAttributes.length > 0) {
            groupedList.push({
                id: 0,
                title: compareMessages.importantTitle.text,
                attributes: this.getAttributeItems(importantAttributes),
            });
        }

        Object.keys(groupedAttributes).forEach((key) => {
            const attributes = groupedAttributes[key];
            const title = groupedAttributes[key].at(0)?.groupName || '';
            groupedList.push({
                id: Number(key),
                title,
                attributes: attributes.map((item) => ({
                    attribute: item.attribute,
                    label: item.label,
                })),
            });
        });

        return groupedList;
    }

    getAttributeItems(attributes: AttributeResponse[]): AttributeItem[] {
        return attributes.map((item) => ({
            attribute: item.attribute,
            label: item.label,
        }));
    }

    getSortedArticlesByCategory(categoryId: number): Article[] {
        const category = this.store?.getCategoryById(categoryId);
        const articles = this.store?.getState().articles || [];
        const articleMap = new Map(articles.map((article) => [article.id, article]));

        return category?.articleIds.map((id) => articleMap.get(id)).filter((item) => item !== undefined) || [];
    }

    renderGroups(wrapper: HTMLElement, groups: AttributeGroup[], articles: Article[]) {
        const groupTemplate = document.querySelector<HTMLTemplateElement>('[data-tk-compare-group-template]');
        const groupItemTemplate = document.querySelector<HTMLTemplateElement>('[data-tk-compare-group-item-template]');
        const groupItemLabelTemplate = document.querySelector<HTMLTemplateElement>(
            '[data-tk-compare-group-item-label-template]',
        );

        if (!groupTemplate || !groupItemTemplate || !groupItemLabelTemplate) return;

        groups.forEach((group) => {
            const groupElement = this.createGroupElement(
                group,
                articles,
                groupTemplate,
                groupItemTemplate,
                groupItemLabelTemplate,
            );
            groupElement && wrapper.appendChild(groupElement);
        });
    }

    protected createGroupElement(
        group: AttributeGroup,
        articles: Article[],
        groupTemplate: HTMLTemplateElement,
        groupItemTemplate: HTMLTemplateElement,
        groupItemLabelTemplate: HTMLTemplateElement,
    ) {
        const groupElement = groupTemplate.content.cloneNode(true) as HTMLElement;
        const groupElementTitle = groupElement.querySelector('[data-tk-group-title');
        if (groupElementTitle) groupElementTitle.textContent = group.title;
        const attributeWrapper = groupElement.querySelector('[data-tk-attributes]');
        if (!attributeWrapper) return;

        group.attributes.forEach((attribute) => {
            const groupItemLabelElement = groupItemLabelTemplate.content.cloneNode(true) as HTMLElement;
            const groupItemTitle = groupItemLabelElement.querySelector('[data-tk-attribute-label]');
            if (groupItemTitle) groupItemTitle.textContent = attribute.label;
            const usefulValues = articles
                .map((article) => article.attributes[attribute.attribute])
                .filter((item) => item !== '');
            if (usefulValues.length === 0) return;
            attributeWrapper.appendChild(groupItemLabelElement);

            articles.forEach((article) => {
                const groupItemElement = groupItemTemplate.content.cloneNode(true) as HTMLElement;
                const groupItemLabel = groupItemElement.querySelector('[data-tk-attribute-label]');
                if (groupItemLabel) {
                    groupItemLabel.textContent = attribute.label;
                    groupItemLabel.setAttribute('data-tk-attribute-label', attribute.label);
                }
                const groupItemValue = groupItemElement.querySelector('[data-tk-attribute-value]');
                if (groupItemValue) {
                    groupItemValue.innerHTML = article.attributes[attribute.attribute] || '';
                }

                attributeWrapper.appendChild(groupItemElement);
            });
        });

        return groupElement;
    }

    compare() {
        const labels = this.querySelectorAll('[data-tk-attribute-unique-label]');
        labels.forEach((label) => {
            const name = label.textContent?.trim();
            const values = Array.from(
                label.parentElement?.querySelectorAll<HTMLSpanElement>(
                    `[data-tk-attribute-label="${name}"] ~ [data-tk-attribute-value]`,
                ) || [],
            );
            const uniqueValues = new Set(values.map((value) => value.textContent?.trim()));
            if (uniqueValues.size > 1) {
                values.forEach((value) => {
                    value.classList.add(this.differenceClassName);
                });
            }
        });
    }

    async rerender(action: CompareActions) {
        if (!this.store) return;
        const { currentCategoryId } = this.store.getState();
        const root = document.documentElement;
        const category = this.store.getCategoryById(currentCategoryId);
        this.classList.toggle(this.emptyClassName, category === undefined);
        if (!category) return;

        const countArticles = this.store.getArticleCountByCategoryId(category.id);
        root.style.setProperty('--count-articles', `${countArticles}`);

        const tilesHeader = this.querySelectorAll('[data-tk-compare-tile-header]');
        const tiles = this.querySelectorAll('[data-tk-compare-tile]');
        const tilesHeaderList = Array.from(tilesHeader);
        const tilesList = Array.from(tiles);
        const tilesHeaderToRemove = tilesHeaderList.filter(
            (item) => !category?.articleIds.includes(Number(item.getAttribute('data-tk-article-id'))),
        );
        const tilesToRemove = tilesList.filter(
            (item) => !category?.articleIds.includes(Number(item.getAttribute('data-tk-article-id'))),
        );
        tilesHeaderToRemove.forEach((item) => item.remove());
        tilesToRemove.forEach((item) => item.remove());
        if (action === CompareActions.SWITCH_CATEGORY) {
            await Promise.all([await this.getArticlesByURL(), await this.getAttributesByURL()]);
        }
        this.renderAttributeList();
    }

    renderEmptyAttributeListMessage() {
        return render(`
            <div class="tk-message tk-message--info tk-message--breakout">
                <span class="tk-message__icon">
                    <i class="tk-icon-info"></i>
                </span>
                <span class="tk-message__label">
                    ${compareMessages.noAttributesFound.text}
                </span>
            </div>
        `);
    }
}
